import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { capitalize } from 'lodash';
import { number } from 'prop-types';
import {
  Banner,
  Button,
  CheckmarkCircleIcon,
  CondensedHeader,
  DarkTheme,
  DataTest,
  FluidButtonLayout,
  Gutter,
  IconButton,
  useApi,
} from '@axiom/ui';
import { CurrencyUtil } from '@axiom/utils';

import EmptyDescription from '../EmptyDescription/EmptyDescription';
import { positionShape } from '../../models/position';
import { DateUtil } from '../../utils/date-util';
// eslint-disable-next-line boundaries/element-types
import { ULAftIcon } from '../UnorderedList/UnorderedListStyles';
import { PositionUtil } from '../../utils/position-util';
import { EditPositionStore } from '../../stores/edit-position-store';
import { DeletePositionStore } from '../../stores/delete-position-store';
import { formatDataTestId } from '../../utils/data-test-util';
import { OpportunityProfilePositionContextMenu } from '../OpportunityProfilePositionContextMenu/OpportunityProfilePositionContextMenu';
import { OpportunityStore } from '../../stores/opportunity-store';
import { TaxonomyApi } from '../../api/taxonomy-api';

import { OpportunityProfilePositionsDescriptionForm } from './OpportunityProfilePositionsDescriptionForm';
import {
  OpportunityProfilePositionWrapper,
  OPPOverlay,
  OPPOverlayForm,
  OPPOverlayFormInput,
  OPPUnderlay,
  OPPHeader,
  OPPIcon,
  OPPHeaderContent,
  OPPMainContent,
  OPPRow,
  OPPTitles,
  OPPTitle,
  OPPValues,
  OPPCell,
  OPPDisplayWrapper,
  OPPDivider,
  OPPRowWrapper,
} from './OpportunityProfilePositionsStyles';

export const OpportunityProfilePositionsItem = ({
  position,
  displayIndex,
  opportunity,
  positionsCount,
}) => {
  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());
  const { businessTitles } = taxonomy;
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [isLastOpen, setIsLastOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const descriptionRef = useRef(null);
  const editDescRef = useRef(null);
  const { currency, isFulfillmentActive } = opportunity;

  const [isEditPositionDescriptionOpen, setIsEditPositionDescriptionOpen] =
    useState(false);

  useEffect(() => {
    if (searchParams.get('showMoreEnabled') === position.id) {
      setShowMore(true);
      searchParams.delete('showMoreEnabled');
      setSearchParams(searchParams, { replace: true });
    }

    if (searchParams.get('editPositionDescription') === position.id) {
      setShowMore(true);
      setIsEditPositionDescriptionOpen(true);
      searchParams.delete('editPositionDescription');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  useEffect(() => {
    if (showMore && !isEditPositionDescriptionOpen) {
      descriptionRef?.current?.scrollIntoView?.({ block: 'end' });
    }
  }, [showMore]);

  const handleClickShowMore = () => setShowMore(!showMore);

  const showOverlay = () => {
    setIsOverlayVisible(true);
  };

  const hideOverlay = () => {
    setIsOverlayVisible(false);
  };

  const handleOverlaySubmit = e => {
    e.preventDefault();
    EditPositionStore.save({
      id: position.id,
      opportunityId: position.opportunityId,
      name: new FormData(e.target).get('POSITION_NAME'),
    });
    hideOverlay();
  };
  return (
    <OpportunityProfilePositionWrapper extraMargin={isLastOpen}>
      <OPPUnderlay
        shouldBlur={isOverlayVisible}
        data-test={formatDataTestId('Position')}
      >
        <DarkTheme backgroundName="none">
          <OPPHeader>
            <OPPIcon>
              <CheckmarkCircleIcon
                checked={PositionUtil.isPositionFulfilled(position.candidates)}
                data-test={formatDataTestId('CheckmarkCircleIcon')}
                filled
              />
            </OPPIcon>

            <OPPHeaderContent>
              <CondensedHeader>Position {displayIndex}</CondensedHeader>
              <DataTest value="Name">{position.name}</DataTest>
            </OPPHeaderContent>

            <OpportunityProfilePositionContextMenu
              showEditNameOverlay={showOverlay}
              showDeletePositionModal={() => DeletePositionStore.load(position)}
              isFulfillmentActive={isFulfillmentActive}
              positionCount={positionsCount}
            />
          </OPPHeader>
        </DarkTheme>
        <OPPMainContent data-test={formatDataTestId('OppMainContent')}>
          <OPPRow>
            <OPPTitles>
              <OPPTitle size={4}>
                Role
                <ULAftIcon />
              </OPPTitle>
              <OPPTitle size={4}>
                Start Date
                <ULAftIcon />
              </OPPTitle>
              <OPPTitle size={4}>
                End Date
                <ULAftIcon />
              </OPPTitle>
            </OPPTitles>
            <OPPValues>
              <OPPCell
                size={4}
                title={position.role}
                data-test={formatDataTestId('Role')}
              >
                {position.role}
              </OPPCell>
              <OPPCell
                size={4}
                title={DateUtil.displayDate(position.startDate)}
                data-test={formatDataTestId('OpportunityPositionStartDate')}
              >
                {DateUtil.displayDate(position.startDate)}
              </OPPCell>
              <OPPCell
                size={4}
                title={DateUtil.displayDate(position.endDate)}
                data-test={formatDataTestId('opportunityPositionEndDate')}
              >
                {DateUtil.displayDate(position.endDate)}
              </OPPCell>
            </OPPValues>
          </OPPRow>
          <OPPRow>
            <OPPTitles>
              <OPPTitle size={4}>Reserved Type</OPPTitle>
              <OPPTitle size={4}>
                Billing Unit
                <ULAftIcon />
              </OPPTitle>
              <OPPTitle size={4}>
                Units per Week
                <ULAftIcon />
              </OPPTitle>
              <OPPTitle size={4}>
                Worksite
                <ULAftIcon />
              </OPPTitle>
            </OPPTitles>
            <OPPValues>
              <OPPCell
                size={4}
                title={position.reservedType}
                data-test={formatDataTestId('ReservedType')}
              >
                {position.reservedType}
              </OPPCell>
              <OPPCell
                size={4}
                title={capitalize(position.weeklyTimeUnit)}
                data-test={formatDataTestId('WeeklyTimeUnit')}
              >
                {capitalize(position.weeklyTimeUnit)}
              </OPPCell>
              <OPPCell
                size={4}
                title={position.billingUnitsPerWeek}
                data-test={formatDataTestId('BillingUnitsPerWeek')}
              >
                {position.billingUnitsPerWeek}
              </OPPCell>
              <OPPCell
                size={4}
                title={position.worksite}
                data-test={formatDataTestId('WorkSite')}
              >
                {position.worksite}
              </OPPCell>
            </OPPValues>
          </OPPRow>
          <OPPDisplayWrapper
            style={{ display: showMore ? 'block' : 'none' }}
            data-test={formatDataTestId('closed_Section')}
            data-value={showMore ? 'open' : 'closed'}
          >
            <OPPDivider />
            <OPPRow>
              <OPPTitles>
                <OPPTitle size={3}>Talent Experience</OPPTitle>
                <OPPTitle size={3}>Talent Type</OPPTitle>
              </OPPTitles>
              <OPPValues>
                <OPPCell
                  size={3}
                  title={position.talentExperienceYears}
                  data-test={formatDataTestId('TalentExperienceYears')}
                >
                  {position.talentExperienceYears}
                </OPPCell>
                <OPPCell
                  size={3}
                  title={position.businessTitle}
                  data-test="BusinessTitle"
                >
                  {businessTitles[position.businessTitle]?.displayName ?? '--'}
                </OPPCell>
              </OPPValues>
            </OPPRow>
            <OPPRow>
              <OPPTitles>
                <OPPTitle size={3}>Baseline Cost</OPPTitle>
                <OPPTitle size={3}>Baseline Margin</OPPTitle>
                <OPPTitle size={3}>Baseline List Price</OPPTitle>
              </OPPTitles>
              <OPPValues>
                <OPPCell
                  size={3}
                  title={CurrencyUtil.getFormattedCurrency({
                    value: position.baselineCost,
                    currencyCode: currency,
                  })}
                  data-test={formatDataTestId('BaselineCost')}
                >
                  {CurrencyUtil.getFormattedCurrency({
                    value: position.baselineCost,
                    currencyCode: currency,
                  })}
                </OPPCell>
                <OPPCell
                  size={3}
                  title={position.baselineMargin}
                  data-test={formatDataTestId('BaselineMargin')}
                >
                  {position.baselineMargin}%
                </OPPCell>
                <OPPCell
                  size={3}
                  title={CurrencyUtil.getFormattedCurrency({
                    value: position.baselineBillRate,
                    currencyCode: currency,
                  })}
                  data-test={formatDataTestId('BaselineBillRate')}
                >
                  {CurrencyUtil.getFormattedCurrency({
                    value: position.baselineBillRate,
                    currencyCode: currency,
                  })}
                </OPPCell>
              </OPPValues>
            </OPPRow>
            {!!position.aiDescription &&
              position.description.includes(position.aiDescription) && (
                <OPPRow>
                  <Banner>
                    This auto generated position description received a high
                    rating. Any AI matches found will be shown in the “Talent”
                    tab.
                  </Banner>
                </OPPRow>
              )}
            <OPPRow>
              <OPPRowWrapper>
                <OPPTitle>
                  Position Description
                  <ULAftIcon />
                </OPPTitle>
                {!isEditPositionDescriptionOpen && (
                  <IconButton
                    pattern="secondary"
                    variation="minimal"
                    icon="pencil"
                    name="EDITDESCRIPTION"
                    onClick={() => {
                      setIsEditPositionDescriptionOpen(
                        !isEditPositionDescriptionOpen
                      );
                    }}
                  />
                )}
              </OPPRowWrapper>
            </OPPRow>
            <Gutter bottom="16px" horizontal="16px" ref={editDescRef}>
              {position.description || isEditPositionDescriptionOpen ? (
                <OpportunityProfilePositionsDescriptionForm
                  position={position}
                  isEditPositionDescriptionOpen={isEditPositionDescriptionOpen}
                  onClose={() => {
                    setIsEditPositionDescriptionOpen(false);
                  }}
                />
              ) : (
                <EmptyDescription header="No Position Description" />
              )}
            </Gutter>
          </OPPDisplayWrapper>
          <Button
            pattern="secondary"
            variation="minimal"
            fluid
            onClick={handleClickShowMore}
            name="SHOWMORE"
            icon={showMore ? 'arrow-up' : 'arrow-right'}
          >
            {showMore ? 'Show Less' : 'Show More'}
          </Button>
          <Gutter bottom="16px" />
        </OPPMainContent>
      </OPPUnderlay>
      {!!isOverlayVisible && (
        <OPPOverlay>
          <OPPOverlayForm onSubmit={handleOverlaySubmit}>
            <OPPOverlayFormInput
              name="POSITION_NAME"
              label="Position Name"
              dataTest="editInput"
              id={`opportunityProfilePositionNameInput-${position.opportunityId}-${position.id}`}
              defaultValue={position.name}
            />
            <Gutter bottom="16px" />
            <FluidButtonLayout>
              <Button
                onClick={hideOverlay}
                name="EDITCANCEL"
                variation="outline"
              >
                Cancel
              </Button>
              <Button text="Submit" type="submit" name="EDITSAVE">
                Submit
              </Button>
            </FluidButtonLayout>
          </OPPOverlayForm>
        </OPPOverlay>
      )}
    </OpportunityProfilePositionWrapper>
  );
};

OpportunityProfilePositionsItem.defaultProps = {};

OpportunityProfilePositionsItem.propTypes = {
  displayIndex: number.isRequired,
  opportunity: OpportunityStore.getDataShape().isRequired,
  position: positionShape.isRequired,
  positionsCount: number,
};
